import { useCallback, useEffect, useRef } from 'react';
import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { authMeQueryOptions } from '@features/auth';
import { api, getErrorMessage, isApiError } from '@shared/api';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  Container,
  Heading,
  Spinner,
  Stack,
  Text,
} from '@shared/ui';

const cliAuthSearchSchema = z.object({
  callback_url: z.string().optional(),
  state: z.string().optional(),
});

type CliAuthorizeResponse = {
  success: true;
  redirectUrl: string;
  expiresAt: string;
};

export const Route = createFileRoute('/$locale/cli-auth')({
  component: CliAuthRoute,
  validateSearch: cliAuthSearchSchema,
});

export function buildCurrentPath(pathname: string, searchStr: string) {
  return searchStr.length > 0 ? `${pathname}${searchStr}` : pathname;
}

export function isUnauthorizedCliAuthError(error: unknown) {
  return isApiError(error) && error.status === 401;
}

export function CliAuthRoute() {
  const { locale } = Route.useParams();
  const search = Route.useSearch();
  const location = useLocation();
  const navigate = useNavigate();
  const authQuery = useQuery({
    ...authMeQueryOptions(),
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const startedRef = useRef(false);
  const redirectedToLoginRef = useRef(false);

  const redirectToLogin = useCallback(() => {
    if (redirectedToLoginRef.current) return;

    redirectedToLoginRef.current = true;
    void navigate({
      to: '/$locale/login',
      params: { locale },
      search: {
        redirect: buildCurrentPath(location.pathname, location.searchStr),
      },
      replace: true,
    });
  }, [locale, location.pathname, location.searchStr, navigate]);

  const authorizeMutation = useMutation({
    mutationFn: async (payload: { callbackUrl: string; state: string }) =>
      await api.post<CliAuthorizeResponse, typeof payload>('/api/v1/auth/cli/authorize', payload),
    onSuccess: (response) => {
      window.location.replace(response.redirectUrl);
    },
    onError: (error) => {
      if (isUnauthorizedCliAuthError(error)) {
        redirectToLogin();
      }
    },
  });

  const callbackUrl = search.callback_url;
  const state = search.state;
  const hasRequiredParams =
    typeof callbackUrl === 'string' &&
    callbackUrl.length > 0 &&
    typeof state === 'string' &&
    state.length > 0;

  const isCheckingAuth = authQuery.isLoading || authQuery.isFetching;
  const isAuthQueryUnauthorized = isUnauthorizedCliAuthError(authQuery.error);
  const isUnauthenticated =
    authQuery.data === null || authQuery.data?.user === null || isAuthQueryUnauthorized;

  useEffect(() => {
    if (isCheckingAuth) return;
    if (!isUnauthenticated) return;

    redirectToLogin();
  }, [isCheckingAuth, isUnauthenticated, redirectToLogin]);

  useEffect(() => {
    if (!hasRequiredParams) return;
    if (isCheckingAuth) return;
    if (isUnauthenticated || authQuery.data === undefined) return;
    if (startedRef.current) return;

    startedRef.current = true;
    authorizeMutation.mutate({
      callbackUrl,
      state,
    });
  }, [
    authQuery.data,
    authorizeMutation,
    callbackUrl,
    hasRequiredParams,
    isCheckingAuth,
    isUnauthenticated,
    state,
  ]);

  const isLoading = isCheckingAuth || authorizeMutation.isPending;
  const isAuthorizeUnauthorized = isUnauthorizedCliAuthError(authorizeMutation.error);
  const errorMessage =
    authorizeMutation.error && !isAuthorizeUnauthorized
      ? getErrorMessage(authorizeMutation.error)
      : null;

  return (
    <>
      <title>Move Data CLI Auth | Molchanovs Move</title>

      <Container size="sm" className="flex min-h-screen items-center justify-center py-10">
        <Card className="w-full">
          <CardContent className="space-y-6 pt-6">
            <Stack gap="sm" className="text-center">
              <Heading as="h1" size="xl">
                Move Data CLI
              </Heading>
              <Text size="sm" color="muted">
                {isLoading ? 'Authorizing your CLI session...' : 'CLI authorization'}
              </Text>
            </Stack>

            {!hasRequiredParams && (
              <Alert variant="destructive">
                <AlertDescription>Invalid CLI authorization request.</AlertDescription>
              </Alert>
            )}

            {errorMessage && (
              <Alert variant="destructive">
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}

            {isLoading && (
              <div className="flex items-center justify-center py-4">
                <Spinner aria-label="Authorizing CLI session" />
              </div>
            )}

            {errorMessage && hasRequiredParams && (
              <Button
                className="w-full"
                onClick={() => {
                  startedRef.current = false;
                  authorizeMutation.reset();
                  authorizeMutation.mutate({ callbackUrl, state });
                }}
              >
                Try again
              </Button>
            )}
          </CardContent>
        </Card>
      </Container>
    </>
  );
}
